import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEO from '../components/common/SEO';
import SafeIcon from '../components/common/SafeIcon';
import SearchWithSuggestions from '../components/common/SearchWithSuggestions';
import PokemonGrid from '../components/pokemon/PokemonGrid';
import TypeBadge from '../components/pokemon/TypeBadge';
import { pokemonData, pokemonTypes, getRandomPokemon, getTopPokemonByStats } from '../data/pokemonData';

const { FiSearch, FiBarChart3, FiTrendingUp, FiShield, FiZap, FiHeart } = FiIcons;

function HomePage() {
  const [featuredPokemon, setFeaturedPokemon] = useState([]);
  const [topStats, setTopStats] = useState({});

  useEffect(() => {
    // Get random featured Pokemon
    setFeaturedPokemon(getRandomPokemon(8));
    
    // Get top Pokemon for each stat
    setTopStats({
      fastest: getTopPokemonByStats('speed', 5),
      strongest: getTopPokemonByStats('attack', 5),
      bulkiest: getTopPokemonByStats('hp', 5),
      defensive: getTopPokemonByStats('defense', 5),
      specialAttack: getTopPokemonByStats('specialAttack', 5)
    });
  }, []);

  const heroSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Pokemon Stats Database",
    "description": "Complete Pokemon Stats Database with 1,215+ Pokemon, detailed statistics, abilities, types, and comparison tools",
    "url": "https://example.com",
    "logo": "https://example.com/logo.png",
    "sameAs": [
      "https://twitter.com/example",
      "https://facebook.com/example"
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen"
    >
      <SEO
        title="Pokemon Stats Database - Complete Pokemon Reference"
        description="Comprehensive database of 1,215+ Pokemon with detailed stats, abilities, types, and comparison tools. Your ultimate Pokemon reference guide."
        keywords="pokemon stats, pokemon database, pokemon abilities, pokemon types, pokemon comparison, pokemon statistics"
        schema={heroSchema}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 text-white py-20" aria-labelledby="hero-heading">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h1 id="hero-heading" className="text-4xl md:text-6xl font-bold mb-6">
              Pokemon Stats Database
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto">
              Complete database of <strong>1,215+ Pokemon</strong> with detailed statistics, abilities, types, and comparison tools
            </p>

            {/* Hero Search */}
            <div className="max-w-2xl mx-auto mb-8">
              <SearchWithSuggestions 
                idPrefix="hero-search" 
                isHero={true} 
                className="w-full"
              />
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <div className="text-2xl font-bold">1,215+</div>
                <div className="text-blue-100">Pokemon</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <div className="text-2xl font-bold">18</div>
                <div className="text-blue-100">Types</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <div className="text-2xl font-bold">200+</div>
                <div className="text-blue-100">Abilities</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <div className="text-2xl font-bold">9</div>
                <div className="text-blue-100">Generations</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <main id="main-content">
        {/* Featured Pokemon Section */}
        <section className="py-16 bg-white" aria-labelledby="featured-heading">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 id="featured-heading" className="text-3xl font-bold text-center mb-12 text-gray-900">
                Featured Pokemon
              </h2>
              <PokemonGrid pokemon={featuredPokemon} />
              
              <div className="text-center mt-8">
                <Link
                  to="/pokemon"
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500"
                >
                  <span>View All Pokemon</span>
                  <SafeIcon icon={FiTrendingUp} className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Pokemon Types Section */}
        <section className="py-16 bg-gray-50" aria-labelledby="types-heading">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <h2 id="types-heading" className="text-3xl font-bold text-center mb-12 text-gray-900">
                Pokemon Types
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {pokemonTypes.map((type, index) => (
                  <motion.div
                    key={type.name}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-lg shadow-md p-4 text-center hover:shadow-lg transition-shadow"
                  >
                    <Link to={`/type/${type.name}`} className="block focus:ring-2 focus:ring-blue-500 rounded-lg">
                      <TypeBadge type={type.name} size="lg" clickable={false} />
                      <div className="mt-2 text-sm text-gray-600">
                        {type.count} Pokemon
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
              <div className="text-center mt-8">
                <Link
                  to="/types"
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors focus:ring-2 focus:ring-purple-500"
                >
                  <span>Explore All Types</span>
                  <SafeIcon icon={FiBarChart3} className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Top Rankings Section */}
        <section className="py-16 bg-white" aria-labelledby="rankings-heading">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <h2 id="rankings-heading" className="text-3xl font-bold text-center mb-12 text-gray-900">
                Top Pokemon Rankings
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Fastest Pokemon */}
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-6 border border-yellow-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <SafeIcon icon={FiZap} className="w-6 h-6 text-yellow-600" />
                    <h3 className="text-xl font-semibold text-gray-900">Fastest Pokemon</h3>
                  </div>
                  <div className="space-y-3">
                    {topStats.fastest?.slice(0, 3).map((pokemon, index) => (
                      <div key={pokemon.id} className="flex items-center justify-between">
                        <Link 
                          to={`/pokemon/${pokemon.name}`}
                          className="flex items-center space-x-2 hover:text-blue-600 transition-colors focus:ring-2 focus:ring-blue-500 rounded"
                        >
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                            index === 0 ? 'medal-gold' : index === 1 ? 'medal-silver' : 'medal-bronze'
                          }`}>
                            {index + 1}
                          </span>
                          <span className="font-medium">{pokemon.displayName}</span>
                        </Link>
                        <span className="font-bold text-yellow-600">{pokemon.baseStats.speed}</span>
                      </div>
                    ))}
                  </div>
                  <Link 
                    to="/top/fastest"
                    className="inline-block mt-4 text-yellow-600 hover:text-yellow-700 font-medium text-sm focus:ring-2 focus:ring-yellow-500 rounded"
                  >
                    View Top 100 →
                  </Link>
                </div>

                {/* Strongest Pokemon */}
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-6 border border-red-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-red-600" />
                    <h3 className="text-xl font-semibold text-gray-900">Strongest Pokemon</h3>
                  </div>
                  <div className="space-y-3">
                    {topStats.strongest?.slice(0, 3).map((pokemon, index) => (
                      <div key={pokemon.id} className="flex items-center justify-between">
                        <Link 
                          to={`/pokemon/${pokemon.name}`}
                          className="flex items-center space-x-2 hover:text-blue-600 transition-colors focus:ring-2 focus:ring-blue-500 rounded"
                        >
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                            index === 0 ? 'medal-gold' : index === 1 ? 'medal-silver' : 'medal-bronze'
                          }`}>
                            {index + 1}
                          </span>
                          <span className="font-medium">{pokemon.displayName}</span>
                        </Link>
                        <span className="font-bold text-red-600">{pokemon.baseStats.attack}</span>
                      </div>
                    ))}
                  </div>
                  <Link 
                    to="/top/strongest"
                    className="inline-block mt-4 text-red-600 hover:text-red-700 font-medium text-sm focus:ring-2 focus:ring-red-500 rounded"
                  >
                    View Top 100 →
                  </Link>
                </div>

                {/* Bulkiest Pokemon */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <SafeIcon icon={FiHeart} className="w-6 h-6 text-green-600" />
                    <h3 className="text-xl font-semibold text-gray-900">Bulkiest Pokemon</h3>
                  </div>
                  <div className="space-y-3">
                    {topStats.bulkiest?.slice(0, 3).map((pokemon, index) => (
                      <div key={pokemon.id} className="flex items-center justify-between">
                        <Link 
                          to={`/pokemon/${pokemon.name}`}
                          className="flex items-center space-x-2 hover:text-blue-600 transition-colors focus:ring-2 focus:ring-blue-500 rounded"
                        >
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                            index === 0 ? 'medal-gold' : index === 1 ? 'medal-silver' : 'medal-bronze'
                          }`}>
                            {index + 1}
                          </span>
                          <span className="font-medium">{pokemon.displayName}</span>
                        </Link>
                        <span className="font-bold text-green-600">{pokemon.baseStats.hp}</span>
                      </div>
                    ))}
                  </div>
                  <Link 
                    to="/top/bulkiest"
                    className="inline-block mt-4 text-green-600 hover:text-green-700 font-medium text-sm focus:ring-2 focus:ring-green-500 rounded"
                  >
                    View Top 100 →
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Quick Tools Section */}
        <section className="py-16 bg-gray-50" aria-labelledby="tools-heading">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <h2 id="tools-heading" className="text-3xl font-bold text-center mb-12 text-gray-900">
                Pokemon Tools
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <Link
                  to="/search"
                  className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow focus:ring-2 focus:ring-blue-500 group"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                      <SafeIcon icon={FiSearch} className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Advanced Search</h3>
                  </div>
                  <p className="text-gray-600">
                    Search Pokemon by name, type, abilities, or stats with powerful filters.
                  </p>
                </Link>

                <Link
                  to="/compare"
                  className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow focus:ring-2 focus:ring-blue-500 group"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                      <SafeIcon icon={FiBarChart3} className="w-6 h-6 text-purple-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Pokemon Compare</h3>
                  </div>
                  <p className="text-gray-600">
                    Compare up to 4 Pokemon side-by-side with detailed stat analysis.
                  </p>
                </Link>

                <Link
                  to="/top/fastest"
                  className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow focus:ring-2 focus:ring-blue-500 group"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="p-3 bg-yellow-100 rounded-lg group-hover:bg-yellow-200 transition-colors">
                      <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-yellow-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Top Rankings</h3>
                  </div>
                  <p className="text-gray-600">
                    Discover the fastest, strongest, and most defensive Pokemon.
                  </p>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </motion.div>
  );
}

export default HomePage;